import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'
import type { components } from '../types/prove-v1'

export const usePermissionUsersQuery = () => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['users', 'permission-directory'],
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const { data, error } = await authFetch().GET('/users', { signal })
      if (error) throwOnError(error)
      return data
    }
  })
}

export const useGetUserPermissionsQuery = (userId: string) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['users', userId, 'permissions'],
    queryFn: async ({ signal }) => {
      const { data, error } = await authFetch().GET(
        '/users/{userId}/permissions',
        {
          params: { path: { userId } },
          signal
        }
      )
      if (error) throwOnError(error)
      return data
    }
  })
}

export const useAddUserPermissionsMutation = () => {
  const { authFetch } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      userId,
      permissions
    }: {
      userId: string
      permissions: components['schemas']['AddUserPermission'][]
    }) => {
      const { data, error } = await authFetch().POST(
        '/users/{userId}/permissions',
        {
          params: { path: { userId } },
          body: {
            permissions
          }
        }
      )
      if (error) throwOnError(error)
      return data
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['users'] }),
        queryClient.invalidateQueries({ queryKey: ['effective-access'] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-groups'] }),
        queryClient.invalidateQueries({ queryKey: ['plans'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['taskDelegation'] })
      ])
  })
}

export const useDeleteUserPermissionMutation = () => {
  const { authFetch } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      userId,
      permissionId
    }: {
      userId: string
      permissionId: number
    }) => {
      const { error } = await authFetch().DELETE(
        '/users/{userId}/permissions/{permissionId}',
        {
          params: { path: { userId, permissionId } }
        }
      )
      if (error) throwOnError(error)
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['users'] }),
        queryClient.invalidateQueries({ queryKey: ['effective-access'] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-groups'] }),
        queryClient.invalidateQueries({ queryKey: ['plans'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['taskDelegation'] })
      ])
  })
}
